// The floating compose bar (VISUAL-DESIGN §6). The box is authoritative while the reader types:
// it is an uncontrolled-in-spirit textarea whose value only the reader and explicit actions
// (send, completion, formatting, draft restore) change — the 2026-09-30 "it ate my letters" bug
// was a box overwritten by a value one render behind.
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { userDisplayName } from '../../core/channelListDerivation';
import * as ComposeFormatting from '../../core/composeFormatting';
import * as EmojiComposer from '../../core/emojiComposer';
import EmojiTable from '../../core/generated/emojiCharacters.json';
import * as MentionComposer from '../../core/mentionComposer';
import type { SendExtras } from '../../bridge/protocol';
import type { UserRecord } from '../../core/records';
import { Icon, type IconName } from '../../design/icons';
import { t } from '../../i18n/i18n';
import { Avatar } from '../Avatar';
import { useComposerAttachments } from '../features/attachments/ComposerAttachments';
import { useVoiceComposer } from '../features/voice/useVoiceComposer';
import { ComposerEmojiButton } from '../features/emoji/ComposerEmojiButton';
import { useComposeFeatures, type ComposeSuggestion } from '../features/compose/useComposeFeatures';
import { EmojiPicker as EmojiPickerModel } from '../../core/emojiPicker';

const EMOJI_NAMES = Object.keys(EmojiTable as Record<string, string>);
const DRAFT_DEBOUNCE_MS = 400;

export interface ComposerProps {
  channelKey: string;
  /** The conversation (and thread root) this box sends into — uploads, commands and forwards need it. */
  channelId: string;
  rootId: string | null;
  initialDraft: string;
  placeholder: string;
  participants: UserRecord[];
  customEmojiNames: string[];
  onSend(message: string, extras: SendExtras): void;
  onDraft(message: string): void;
  onTyping(): void;
  disabledNotice?: string | null;
  banner?: ReactNode;
}

type Suggestion =
  | { kind: 'mention'; active: MentionComposer.ActiveMention; items: Array<{ username: string; label: string; userId: string | null; version: number }> }
  | { kind: 'emoji'; active: EmojiComposer.Active; items: Array<{ name: string; char: string | null }> }
  | ComposeSuggestion;

export function Composer(props: ComposerProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState(props.initialDraft);
  const [cursor, setCursor] = useState(props.initialDraft.length);
  const [highlight, setHighlight] = useState(0);
  const draftTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(text);
  latest.current = text;
  const wrap = useRef<HTMLDivElement>(null);
  // Files: 📎, paste, drop on the pane, the tray (features/attachments).
  const attach = useComposerAttachments({ channelKey: props.channelKey, channelId: props.channelId, anchor: wrap, disabled: !!props.disabledNotice });
  // Voice notes: the mic beside send while the box is empty, the strip replacing the field (features/voice).
  const voice = useVoiceComposer({ channelKey: props.channelKey, channelId: props.channelId, disabled: !!props.disabledNotice, onSend: props.onSend });

  // The timeline's bottom inset is the bar's MEASURED height (it grows with the draft), handed to
  // the pane as --composer-space so the last message is never under the floating bar.
  useLayoutEffect(() => {
    const el = wrap.current;
    const pane = el?.parentElement;
    if (!el || !pane) return;
    const apply = () => pane.style.setProperty('--composer-space', `${el.offsetHeight + 16}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [props.disabledNotice]);

  // A different conversation: flush the old draft, take the new one.
  useEffect(() => {
    setText(props.initialDraft);
    setCursor(props.initialDraft.length);
    requestAnimationFrame(() => ref.current?.focus());
    return () => {
      if (draftTimer.current) {
        clearTimeout(draftTimer.current);
        draftTimer.current = null;
        props.onDraft(latest.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.channelKey]);

  // Grow with the draft up to ~5 lines, then scroll (the bar's height is MEASURED, never a constant).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = Math.min(180, el.scrollHeight) + 'px';
  }, [text]);

  const candidates = useMemo(
    () => props.participants.filter((u) => u.deleteAt === 0).map((u) => MentionComposer.candidate(u, userDisplayName)),
    [props.participants],
  );
  const usersByName = useMemo(() => new Map(props.participants.map((u) => [u.username, u])), [props.participants]);
  // Slash commands, ~channels, priority, ↑-to-edit (features/compose).
  const compose = useComposeFeatures({ channelId: props.channelId, rootId: props.rootId, text, cursor, textarea: ref, place: (v, c) => place(v, c) });

  const suggestion: Suggestion | null = useMemo(() => {
    const mention = MentionComposer.activeMention(text, cursor);
    if (mention) {
      const people = MentionComposer.filterCandidates(candidates, mention.query, 6).map((c) => ({
        username: c.username,
        label: c.displayName,
        userId: c.userId,
        version: usersByName.get(c.username)?.lastPictureUpdate ?? 0,
      }));
      const specials = MentionComposer.matchingSpecials(mention.query).map((s) => ({ username: s, label: '', userId: null, version: 0 }));
      const items = [...people, ...specials];
      return items.length ? { kind: 'mention', active: mention, items } : null;
    }
    const emoji = EmojiComposer.active(text, cursor);
    if (emoji) {
      const names = EmojiComposer.filter([...props.customEmojiNames, ...EMOJI_NAMES], emoji.query, 8);
      const items = names.map((name) => ({ name, char: (EmojiTable as Record<string, string>)[name] ?? null }));
      return items.length ? { kind: 'emoji', active: emoji, items } : null;
    }
    return cursor < 0 ? null : compose.suggestion(text, cursor);
  }, [text, cursor, candidates, usersByName, props.customEmojiNames, compose.suggestion]);

  useEffect(() => setHighlight(0), [suggestion?.kind, suggestion?.items.length]);

  const update = (value: string, caret: number) => {
    setText(value);
    setCursor(caret);
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => {
      draftTimer.current = null;
      props.onDraft(value);
    }, DRAFT_DEBOUNCE_MS);
  };

  const place = (value: string, caret: number) => {
    update(value, caret);
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const accept = (index: number) => {
    if (!suggestion) return;
    if (suggestion.kind === 'command' || suggestion.kind === 'channel') {
      const applied = compose.accept(suggestion, index, text, cursor);
      return place(applied.text, applied.cursor);
    }
    if (suggestion.kind === 'mention') {
      const item = suggestion.items[index];
      const applied = MentionComposer.apply(text, cursor, suggestion.active, item.username);
      place(applied.text, applied.cursor);
    } else {
      const item = suggestion.items[index];
      // A custom emoji has no character: it stays a `:name:` shortcode on the wire.
      const applied = EmojiComposer.apply(text, cursor, suggestion.active, item.char ?? `:${item.name}: `);
      place(applied.text, applied.cursor);
    }
  };

  const send = () => {
    const message = text.trim();
    // A bare attachment is a complete message; a failed upload holds the send (features/attachments).
    const gate = attach.gate(message);
    if (gate === 'empty' || gate === 'blocked') return;
    const extras: SendExtras = {};
    // A command cannot take a file (ChannelViewModel.sendMessage): with files, "/x" is a message.
    const withFiles = attach.hasFiles();
    if (withFiles || !compose.trySend(message, extras)) {
      props.onSend(message, { ...extras, ...compose.extras(), ...(withFiles ? { fileIds: attach.take() } : {}) });
      compose.sent();
    }
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = null;
    props.onDraft('');
    place('', 0);
  };

  // The emoji picker's pick, at the caret (replacing a selection), through place() — the box's
  // own value and selection are read at the moment of the pick, never a render-old copy.
  const insertEmoji = (name: string, custom: boolean) => {
    const el = ref.current;
    const value = el ? el.value : latest.current;
    const start = el ? el.selectionStart : value.length;
    const end = el ? el.selectionEnd : value.length;
    const applied = EmojiPickerModel.insertAtCaret(value, start, end, name, custom);
    place(applied.text, applied.caret);
  };

  const format = (f: ComposeFormatting.Format) => {
    const el = ref.current;
    if (!el) return;
    const applied = ComposeFormatting.toggle(text, el.selectionStart, el.selectionEnd, f);
    update(applied.text, applied.selectionEnd);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(applied.selectionStart, applied.selectionEnd);
    });
  };

  if (props.disabledNotice) {
    return (
      <div className="composer-wrap" ref={wrap}>
        <div className="read-only-notice">{props.disabledNotice}</div>
      </div>
    );
  }

  const sendGate = attach.gate(text);
  const ready = sendGate === 'ready' || sendGate === 'queued';
  const tools: Array<[ComposeFormatting.Format, IconName, string]> = [
    ['bold', 'bold', t('channel.format.bold')],
    ['italic', 'italic', t('channel.format.italic')],
    ['strikethrough', 'strike', t('channel.format.strikethrough')],
    ['code', 'code', t('channel.format.code')],
  ];

  return (
    <div className="composer-wrap" ref={wrap}>
      {props.banner}
      {voice.notice}
      {compose.above}
      {suggestion ? (
        <div className="autocomplete" role="listbox">
          {suggestion.kind === 'command' || suggestion.kind === 'channel'
            ? compose.renderItems(suggestion, highlight, accept)
            : suggestion.kind === 'mention'
            ? suggestion.items.map((item, i) => (
                <button key={item.username} className="item" role="option" aria-selected={i === highlight} onMouseDown={(e) => (e.preventDefault(), accept(i))}>
                  {item.userId ? <Avatar userId={item.userId} name={item.label || item.username} size={22} version={item.version} /> : <Icon name="at" size={18} />}
                  <span>{item.label || '@' + item.username}</span>
                  <span className="sub">@{item.username}</span>
                </button>
              ))
            : suggestion.items.map((item, i) => (
                <button key={item.name} className="item" role="option" aria-selected={i === highlight} onMouseDown={(e) => (e.preventDefault(), accept(i))}>
                  <span style={{ width: 22, textAlign: 'center', fontSize: 17 }}>{item.char ?? '·'}</span>
                  <span>:{item.name}:</span>
                </button>
              ))}
        </div>
      ) : null}
      <div className={`composer${voice.recording ? ' voice-recording' : ''}`}>
        {attach.tray}
        {voice.strip}
        {compose.fieldLine}
        <textarea
          ref={ref}
          rows={1}
          value={text}
          placeholder={props.placeholder}
          aria-label={t('channel.field.message')}
          onPaste={attach.onPaste}
          spellCheck
          onChange={(e) => {
            update(e.target.value, e.target.selectionStart);
            props.onTyping();
          }}
          onSelect={(e) => setCursor((e.target as HTMLTextAreaElement).selectionStart)}
          onKeyDown={(e) => {
            if (suggestion) {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlight((h) => (h + 1) % suggestion.items.length);
                return;
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlight((h) => (h - 1 + suggestion.items.length) % suggestion.items.length);
                return;
              }
              if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                accept(highlight);
                return;
              }
              if (e.key === 'Escape') {
                e.stopPropagation(); // dismissing the strip must not also close the thread pane
                setCursor(-1);
                return;
              }
            }
            if (!suggestion && compose.keyDown(e, text)) return;
            // Enter sends, Shift+Enter breaks the line; an IME composition (dead keys, Japanese)
            // owns Enter until it commits — the Mac's accent bug, avoided at the source.
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              send();
              return;
            }
            if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey) {
              const k = e.key.toLowerCase();
              if (k === 'b') (e.preventDefault(), format('bold'));
              else if (k === 'i') (e.preventDefault(), format('italic'));
            }
          }}
        />
        <div className="composer-tools">
          {attach.button}
          {tools.map(([f, icon, label]) => (
            <button key={f} className="icon-button" title={label} aria-label={label} onMouseDown={(e) => e.preventDefault()} onClick={() => format(f)}>
              <Icon name={icon} size={16} />
            </button>
          ))}
          <ComposerEmojiButton onPick={insertEmoji} onClosed={() => ref.current?.focus()} />
          {compose.toolbar}
          <span className="spacer" />
          {voice.micButton(!text.trim() && !attach.hasFiles())}
          <button className={`send-button${ready ? ' ready' : ''}`} disabled={!ready} onClick={send} title={attach.sendHint(sendGate) ?? t('mac.send')} aria-label={t('mac.send')}>
            <Icon name="send" size={17} />
          </button>
        </div>
      </div>
    </div>
  );
}
